/**
 * SchemaTypeMappingDialog — Modal for defining custom data type mapping rules.
 *
 * Users can define rules like:
 *   DECIMAL(99,0) → DECIMAL(9,0) (scope: all tables or specific table)
 *
 * Rules are applied during conversion, between fixTypes and dialectConvert steps.
 */

import { useState } from 'react'
import { X, Plus, Trash2, Zap } from 'lucide-react'

function SchemaTypeMappingDialog({ isOpen, onClose, rules, onRulesChange, actionItems = [] }) {
  const [localRules, setLocalRules] = useState(rules || [])

  if (!isOpen) return null

  const addRule = () => {
    setLocalRules(prev => [
      ...prev,
      { id: Date.now(), sourceType: '', targetType: '', scope: 'all' }
    ])
  }

  const updateRule = (id, field, value) => {
    setLocalRules(prev =>
      prev.map(r => r.id === id ? { ...r, [field]: value } : r)
    )
  }

  const removeRule = (id) => {
    setLocalRules(prev => prev.filter(r => r.id !== id))
  }

  const handleApply = () => {
    onRulesChange(localRules.filter(r => r.sourceType && r.targetType))
    onClose()
  }

  // Suggest rules based on action items (unfixed DECIMAL issues)
  const suggestFromActionItems = () => {
    const decimalIssues = actionItems.filter(a =>
      a.issue?.includes('DECIMAL') && a.issue?.includes('invalid precision')
    )
    if (decimalIssues.length === 0) return

    // Extract unique source types from action items
    const seen = new Set()
    const suggestions = []
    for (const item of decimalIssues) {
      const match = item.issue?.match(/DECIMAL\((\d+),?\s*(\d+)\)/)
      if (match) {
        const sourceType = `DECIMAL(${match[1]}, ${match[2]})`
        if (!seen.has(sourceType)) {
          seen.add(sourceType)
          suggestions.push({
            id: Date.now() + suggestions.length,
            sourceType,
            targetType: 'DECIMAL(9, 0)',
            scope: 'all',
          })
        }
      }
    }

    if (suggestions.length > 0) {
      setLocalRules(prev => [...prev, ...suggestions])
    }
  }

  return (
    <div className="schema-deploy-overlay">
      <div className="schema-deploy-dialog" style={{ width: 640 }}>
        {/* Header */}
        <div className="schema-deploy-header">
          <h3>Data Type Mapping Rules</h3>
          <button className="schema-deploy-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="schema-deploy-body">
          <div className="schema-deploy-label" style={{ marginBottom: 12 }}>
            Define custom type mappings applied during conversion.
            These override the default type fixes and remaps.
          </div>

          {/* Rules table */}
          <div className="schema-rules-table">
            <div className="schema-rules-header-row">
              <span className="schema-rules-col-source">Source Type</span>
              <span className="schema-rules-col-arrow" />
              <span className="schema-rules-col-target">Target Type</span>
              <span className="schema-rules-col-scope">Scope</span>
              <span className="schema-rules-col-actions" />
            </div>
            {localRules.length === 0 && (
              <div className="schema-rules-empty">
                No rules defined. Click "Add Rule" or "Suggest Fixes" to get started.
              </div>
            )}
            {localRules.map(rule => (
              <div key={rule.id} className="schema-rules-row">
                <input
                  className="schema-rules-input"
                  value={rule.sourceType}
                  onChange={e => updateRule(rule.id, 'sourceType', e.target.value)}
                  placeholder="e.g. DECIMAL(99, 0)"
                />
                <span className="schema-rules-arrow">→</span>
                <input
                  className="schema-rules-input"
                  value={rule.targetType}
                  onChange={e => updateRule(rule.id, 'targetType', e.target.value)}
                  placeholder="e.g. DECIMAL(9, 0)"
                />
                <input
                  className="schema-rules-input schema-rules-scope"
                  value={rule.scope}
                  onChange={e => updateRule(rule.id, 'scope', e.target.value)}
                  placeholder="all"
                  title="'all' or a specific table name"
                />
                <button
                  className="schema-rules-delete"
                  onClick={() => removeRule(rule.id)}
                  title="Remove rule"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="schema-rules-actions">
            <button className="schema-toolbar-btn" onClick={addRule}>
              <Plus size={14} />
              Add Rule
            </button>
            {actionItems.length > 0 && (
              <button className="schema-toolbar-btn" onClick={suggestFromActionItems}>
                <Zap size={14} />
                Suggest Fixes
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="schema-deploy-footer">
          <button className="schema-deploy-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="schema-deploy-btn-deploy" onClick={handleApply} style={{ background: '#3b82f6' }}>
            Apply Rules
          </button>
        </div>
      </div>
    </div>
  )
}

export default SchemaTypeMappingDialog
